import { VFC } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'
import { getNegativeBadges } from 'src/api/endpoints'
import { sendNegativeBadge } from 'src/api/negativeBadge'
import { asModal, ModalProps } from 'src/components/hoc/asModal'
import {
  trueblack,
  truewhite,
  _formalert,
  _menuicon,
  _profilegreen,
} from 'src/styles/colors'
import { fontWeightBold } from 'src/styles/font'
import styled from 'styled-components'
import { mutate } from 'swr'
import { Button } from './styles'

type ValuesType = {
  reasons: string[]
  reportComment: string
}

const ReportReason: { [key: number]: string } = {
  0: '非決裁者が出てきた',
  1: '一方的に営業ばかりされた',
  2: '遅刻・ドタキャンをされた',
  3: '登録者と違う人が出てきた',
  4: 'その他',
} as const

const Report: VFC<ModalProps<{ counterId: number | undefined }>> = ({
  close,
  counterId,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm()

  const handleOnSubmit: SubmitHandler<ValuesType> = async (values) => {
    if (counterId != undefined) {
      await sendNegativeBadge(
        counterId,
        values.reasons,
        values.reportComment || '',
      )
      await mutate(getNegativeBadges(counterId))
    }
    close()
  }

  return (
    <Layout>
      <Titles />
      <ErrorMessage>
        <p>{errors.reasons && errors.reasons.message}</p>
      </ErrorMessage>
      <Form onSubmit={handleSubmit(handleOnSubmit)}>
        <ReasonDiv>
          <p>通報内容を選択(必須)</p>
          <RadioButtons>
            {Object.keys(ReportReason).map((_, index) => (
              <>
                <input
                  id={String(index)}
                  type="checkbox"
                  {...register('reasons', { required: '通報内容は必須です' })}
                  value={index}
                  name="reasons"
                />
                <label key={index} htmlFor={String(index)}>
                  {Object.values(ReportReason)[index]}
                </label>
              </>
            ))}
          </RadioButtons>
          <br />
        </ReasonDiv>
        <CommentDiv>
          <p>コメント(任意)</p>
          <textarea id="reportComment" {...register('reportComment')} />
        </CommentDiv>
        <ModalButtons>
          <CancelButton type="button" onClick={close}>
            キャンセル
          </CancelButton>
          <ReportButton type="submit" disabled={isSubmitting}>
            通報を送信
          </ReportButton>
        </ModalButtons>
      </Form>
    </Layout>
  )
}

export const ReportModal = asModal(Report, { scrollable: true })

const Titles: VFC = () => (
  <>
    <h2>ユーザーを通報する</h2>
    <p>下記から通報内容を選択してください。</p>
    <p>あなたがこのユーザーを通報したことは相手には通知されません。</p>
  </>
)

const Layout = styled.div`
  position: relative;
  flex-direction: column;
  max-width: min(80vw, 500px);
  padding: 5%;
  border-radius: 12px;
  background-color: ${truewhite};
  line-height: 1.3rem;

  h2 {
    width: 100%;
    padding-bottom: 20px;
    font-size: 20px;
    font-weight: ${fontWeightBold};

    @media screen and (max-width: 540px) {
      font-size: 15px;
    }
  }

  p {
    @media screen and (max-width: 540px) {
      font-size: 10px;
      line-height: 1rem;
    }
  }
`

const Form = styled.form``

const ReasonDiv = styled.div`
  p {
    font-size: 12px;
    color: ${_menuicon};
  }
`

const CommentDiv = styled.div`
  p {
    font-size: 12px;
    color: ${_menuicon};
  }

  textarea {
    width: 100%;
    min-height: 100px;
    padding: 5px;
    border: solid 1px;
    border-radius: 5px;
    color: ${trueblack};
    border: 1px solid ${_menuicon};
    vertical-align: top;
    resize: none;
  }
`

const RadioButtons = styled.div`
  input[type='checkbox'] {
    display: none;
    height: 50px;
  }

  input:checked + label {
    background: ${_profilegreen};
    color: ${truewhite};
  }

  label {
    display: inline-block;
    margin-right: 10px;
    margin-bottom: 10px;
    padding-left: 20px;
    padding-right: 20px;
    width: auto;
    color: ${_profilegreen};
    font-weight: ${fontWeightBold};
    text-align: center;
    line-height: 30px;
    cursor: pointer;
    border: 1px solid ${_menuicon};
    border-radius: 15px;
    white-space: nowrap;
  }
`

const ErrorMessage = styled.div`
  color: ${_formalert};
  margin-top: 15px;
  p {
    text-align: center;
  }
`

const ModalButtons = styled.div`
  display: flex;
  width: 100%;
  margin-top: 20px;
  justify-content: flex-end;

  @media screen and (max-width: 540px) {
    display: block;
  }
`

const ReportButton = styled(Button)`
  color: ${truewhite};
  background: ${_profilegreen};
  height: 33px;
  padding: 0 24px;
`

const CancelButton = styled(Button)`
  color: ${_profilegreen};
  background: ${truewhite};
  border: unset;
  height: 33px;
  padding: 0 24px;
`
